/**
 * Reconciliation Utility Functions
 * Shared across grid, history view, and slice
 */

export const roundCurrency = (value) => Math.round((Number(value) || 0) * 100) / 100;

export const normalizeTinyNegativeBalanceAfter = (value) => {
    const rounded = roundCurrency(value);
    return Math.max(0, rounded);
};

export const isWorkersCompCaseType = (value) => {
    const normalized = String(value || '').trim().toUpperCase();
    return ['WC', 'WORKERS COMP', 'WORKERS_COMP', 'WORKERSCOMP'].includes(normalized);
};

export const isMVACaseType = (value) => {
    const normalized = String(value || '').trim().toUpperCase();
    return ['MVA', 'MOTOR VEHICLE', 'MOTOR_VEHICLE', 'MVA_ACCIDENT'].includes(normalized);
};

export const toDateOnlyKey = (value) => {
    if (!value) return '';
    try {
        let dateValue = null;
        if (typeof value === 'string') {
            if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
            dateValue = new Date(value);
        } else if (value instanceof Date) {
            dateValue = value;
        } else if (typeof value === 'object') {
            if (typeof value.toDate === 'function') {
                dateValue = value.toDate();
            } else if (typeof value._seconds === 'number') {
                dateValue = new Date(value._seconds * 1000);
            } else if (typeof value.seconds === 'number') {
                dateValue = new Date(value.seconds * 1000);
            }
        }
        if (!dateValue || Number.isNaN(dateValue.getTime())) return '';
        return dateValue.toISOString().split('T')[0];
    } catch {
        return '';
    }
};

const RX_STATUS_CONFIG = {
    new: { label: 'New', className: 'bg-blue-100 text-blue-700 border-blue-300' },
    billing_intake: { label: 'Billing Intake', className: 'bg-pink-100 text-pink-700 border-pink-300' },
    bill_processed: { label: 'Bill Processed', className: 'bg-lime-100 text-lime-700 border-lime-300' },
    rebill: { label: 'Rebill', className: 'bg-orange-100 text-orange-700 border-orange-300' },
    denied: { label: 'Denied', className: 'bg-red-100 text-red-700 border-red-300' },
    exhausted: { label: 'Exhausted', className: 'bg-amber-100 text-amber-700 border-amber-300' },
    paid: { label: 'Paid', className: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
    partial_payment: { label: 'Partial Payments', className: 'bg-amber-100 text-amber-700 border-amber-300' },
    reconciled: { label: 'Reconciled', className: 'bg-teal-100 text-teal-700 border-teal-300' },
    canceled: { label: 'Canceled', className: 'bg-gray-100 text-gray-500 border-gray-300' },
    archived: { label: 'Archived', className: 'bg-zinc-100 text-zinc-600 border-zinc-300' },
    closed: { label: 'Closed', className: 'bg-zinc-100 text-zinc-600 border-zinc-300' },
    commercial: { label: 'Commercial', className: 'bg-sky-100 text-sky-700 border-sky-300' },
    merged: { label: 'Merged', className: 'bg-slate-100 text-slate-600 border-slate-300' },
};

export const getAllocationRxStatus = (alloc = {}) => {
    const explicit = String(alloc.medicationStatus || alloc.status || '').trim().toLowerCase();
    if (alloc.markAsReconciled) return 'reconciled';
    if (alloc.isDenied) return 'denied';
    if (alloc.isExhausted) return 'exhausted';
    if (alloc.isWriteOff && ['denied', 'exhausted', 'reconciled'].includes(explicit)) {
        return 'bill_processed';
    }
    if (explicit && RX_STATUS_CONFIG[explicit] && !['new', 'billing_intake', 'bill_processed'].includes(explicit)) {
        return explicit;
    }

    const currentPayment = parseFloat(alloc.currentPayment) || 0;
    if (currentPayment > 0) {
        const billedAmount = parseFloat(alloc.billedAmount) || 0;
        const balanceAfter = parseFloat(alloc.balanceAfter) || 0;
        if (balanceAfter > 0.01 || (billedAmount > 0 && currentPayment < billedAmount * 0.8)) {
            return 'partial_payment';
        }
        return 'paid';
    }

    if (RX_STATUS_CONFIG[explicit]) return explicit;
    return 'bill_processed';
};

/**
 * Checks if a patient name matches the filter query tokens
 */
export const matchesNameFilter = (patientName, queryTokens) => {
    if (!queryTokens || queryTokens.length === 0) return true;
    const name = (patientName || '').toLowerCase();
    return queryTokens.every(token => name.includes(token));
};

/**
 * Tokenizes a name query string for efficient matching
 */
export const getNameSearchTokens = (query) => {
    if (!query) return [];
    return query.toLowerCase().replace(/\s+/g, ' ').trim().split(' ').filter(Boolean);
};

/**
 * Builds searchable tokens from Rx values, stripping -XX suffixes
 */
export const getRxSearchTokens = (...values) => {
    const tokens = new Set();
    values.forEach((value) => {
        const normalized = String(value || '').toLowerCase().trim();
        if (!normalized) return;

        tokens.add(normalized);
        const lastDash = normalized.lastIndexOf('-');
        if (lastDash > 0) {
            const suffix = normalized.slice(lastDash + 1);
            if (/^\d+$/.test(suffix)) {
                tokens.add(normalized.slice(0, lastDash));
            }
        }
    });
    return Array.from(tokens);
};

/**
 * Checks if an allocation's Rx fields match the filter query.
 * Accepts the full allocation object to check rxNumber, fullRxNumber, and medicationId.
 * @param {Object} allocation - The allocation object
 * @param {string} query - Raw filter query string (not pre-tokenized)
 */
export const matchesRxFilter = (allocation, query) => {
    const q = String(query || '').toLowerCase().trim();
    if (!q) return true;

    return getRxSearchTokens(
        allocation?.rxNumber,
        allocation?.fullRxNumber,
        allocation?.medicationId
    ).some((token) => token.includes(q));
};
